import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Dropdown from "src/components/ui/Dropdown";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import { toast } from "react-toastify";
import { ProductCartItem } from "src/types/sales.types";
import { currencyFormatter } from "src/utils/common";
import productApis from "src/api/product";
import { Customer } from "src/types/customer.types";
import customerApis from "src/api/customer";
import salesApis from "src/api/salesOrder";
import { Product } from "src/types/product.types";
import { Trash } from "lucide-react";
import Textarea from "src/components/ui/Textarea";

const SalesForm: React.FC = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const { createSaleOrder, editSaleOrder } = useAppSelector(
    (state) => state.sales
  );

  // ------------------ Customer ------------------
  const [customer, setCustomer] = useState<Customer>({
    name: "",
    mobile: "",
    email: "",
    address: "",
    pincode: "",
  });

  // ------------------ Products ------------------
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<ProductCartItem[]>([]);

  // ------------------ Summary ------------------
  const [discountPercentage, setDiscountPercentage] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [remarks, setRemarks] = useState("");
  const [orderStatus, setOrderStatus] = useState<
    "draft" | "completed" | "cancelled"
  >("completed");

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // ------------------ Effects ------------------
  useEffect(() => {
    const getProducts = async () => {
      try {
        const prodRes = await productApis.getProductList({
          page: 1,
          limit: 1000,
          status: "active",
        });
        const list = prodRes?.data?.data || prodRes?.data || [];
        setProducts(list);
      } catch (error) {
        console.error("Error loading products:", error);
        toast.error("Failed to load products");
      }
    };

    getProducts();
  }, []);

  useEffect(() => {
    const loadOrderDetails = async () => {
      if (!saleId) return;
      try {
        setIsLoadingDetails(true);
        const res = await salesApis.getOrderById(saleId);
        const order = res?.data;

        if (!order) {
          toast.error("Sale order not found");
          navigate("/app/sale/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "draft" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this draft order");
          navigate("/app/sale/list");
          return;
        }

        const orderCustomer = order.customerId || {};
        setCustomer({
          name: orderCustomer.name || "",
          mobile: orderCustomer.mobile || "",
          email: orderCustomer.email || "",
          address: orderCustomer.address || "",
          pincode: orderCustomer.pincode || "",
        });

        setPaymentMode(order.paymentMode || "cash");
        const loadedStatus = order.orderStatus || "completed";
        setOrderStatus(loadedStatus);
        setPaymentStatus("paid");
        setRemarks(order.remarks || "");

        if (order.subTotal && order.discount) {
          const discountPercent = (order.discount / order.subTotal) * 100;
          setDiscountPercentage(discountPercent.toFixed(2));
        }

        const mappedItems: ProductCartItem[] =
          order.items?.map((item: any) => {
            const productIdValue =
              typeof item.productId === "string"
                ? item.productId
                : item.productId?._id || "";
            const sellingPrice = Number(item.sellingPrice || 0);
            const quantity = Number(item.quantity || 0);
            const discountPercent = Number(item.discountPercent || 0);
            const totalPrice =
              item.totalPrice ??
              Number(
                (quantity * sellingPrice * (1 - discountPercent / 100)).toFixed(
                  2
                )
              );
            const taxPercent = Number(
              item.taxPercent ?? item.productId?.gstRate ?? 18
            );

            return {
              productId: productIdValue,
              name: item.productId?.name || item.name || "",
              mrp: Number(item.mrp || sellingPrice || 0),
              sellingPrice,
              discountPercent,
              taxPercent,
              quantity,
              totalPrice,
            };
          }) || [];

        setCart(mappedItems);

        if (loadedStatus !== "draft") {
          toast.error("Only draft sales can be edited");
          navigate("/app/sale/list");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (saleId) {
      loadOrderDetails();
    }
  }, [saleId, navigate, isTenantAdmin, user?._id]);

  const handleClose = useCallback(() => {
    dispatch(salesActions.resetCreateSalesOrder());
    dispatch(salesActions.resetEditSalesOrder());
    navigate("/app/sale/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (createSaleOrder.data) {
      toast.success("Sale order created successfully!");
      handleClose();
    } else if (createSaleOrder.error) {
      toast.error(`Error: ${createSaleOrder.error}`);
    }
  }, [createSaleOrder, handleClose]);

  useEffect(() => {
    if (editSaleOrder.data) {
      toast.success("Sale order updated successfully!");
      handleClose();
    } else if (editSaleOrder.error) {
      toast.error(`Error: ${editSaleOrder.error}`);
    }
  }, [editSaleOrder, handleClose]);

  // ------------------ Customer Lookup ------------------
  const handleSearchCustomer = async () => {
    try {
      const res = await customerApis.getCustomersList({
        search: customer.mobile,
        page: 1,
        limit: 1,
      });

      const found = res?.data?.[0];
      if (found) {
        setCustomer(found);
        toast.success("Existing customer found!");
      } else {
        toast.info("Customer not found, please fill details manually.");
      }
    } catch (error) {
      console.error("Customer search failed", error);
      toast.error("Error searching customer");
    }
  };

  // ------------------ Cart Logic ------------------
  const handleAddProduct = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    if (cart.some((c) => c.productId === productId)) {
      toast.warning("Product already added");
      return;
    }

    const sellingPrice = Number(product.sellingPrice || product.mrp || 0);

    setCart([
      ...cart,
      {
        productId: product._id || "",
        name: product.name || "",
        mrp: Number(product.mrp || sellingPrice || 0),
        sellingPrice,
        discountPercent: 0,
        taxPercent: Number(product.gstRate ?? 18),
        quantity: 1,
        totalPrice: sellingPrice,
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    field: keyof ProductCartItem,
    value: any
  ) => {
    const updated = [...cart];
    // @ts-ignore
    updated[index][field] = value;

    const item = updated[index];
    const qty = parseFloat(String(item.quantity)) || 0;
    const total =
      qty * item.sellingPrice * (1 - (item.discountPercent || 0) / 100);
    updated[index].totalPrice = Number(total.toFixed(2));
    setCart(updated);
  };

  const handleRemoveItem = (index: number) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // ------------------ Totals ------------------
  // Tax is computed per line item using each product's own GST rate, since
  // different products can be taxed at different slabs (0/5/12/18/28%).
  const discountPercentValue = parseFloat(discountPercentage) || 0;
  const subTotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const taxValue = cart.reduce(
    (sum, item) => sum + (item.totalPrice * (item.taxPercent || 0)) / 100,
    0
  );
  const discountAmount = (subTotal * discountPercentValue) / 100;
  const grandTotal = subTotal + taxValue - discountAmount;

  // ------------------ Submit ------------------
  const handleSubmit = (status: "completed" | "draft" = "completed") => {
    if (!customer.name || !customer.mobile) {
      toast.error("Customer name and mobile are required");
      return;
    }
    if (cart.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    if (status === "completed") {
      for (const item of cart) {
        const product = products.find((p) => p._id === item.productId);
        if (!product) continue;
        if (Number(item.quantity) > Number(product.stock || 0)) {
          toast.error(
            `Insufficient stock for ${product.name}. Available: ${product.stock}`
          );
          return;
        }
      }
    }

    const payload = {
      customer,
      products: cart,
      taxAmount: taxValue,
      discount: discountAmount,
      paymentMode,
      paymentStatus: "paid",
      remarks,
      createdBy: user?._id || "",
      orderStatus: status,
    };

    dispatch(
      saleId
        ? salesActions.editSalesOrderRequest({
            id: saleId,
            data: payload,
          })
        : salesActions.createSalesOrderRequest(payload)
    );
  };

  const pageTitle = saleId ? "Edit Sale Order" : "Create Sale Order";

  return (
    <DashboardLayout
      meta={{
        title: saleId
          ? "Edit Sale Order | Invyntra"
          : "Create Sale Order | Invyntra",
        description: "Create sales orders.",
        keywords: "sales orders, invoices, customers, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={pageTitle}
          showBack={!!saleId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Sales Orders", path: "/app/sale/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            {/* ---------------- CUSTOMER INFO ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Customer Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  label="Mobile Number"
                  value={customer.mobile}
                  onChange={(e) =>
                    setCustomer({ ...customer, mobile: e.target.value })
                  }
                  placeholder="Enter mobile number"
                />
                <Button
                  onClick={handleSearchCustomer}
                  className="mt-6 bg-indigo-600 text-white"
                >
                  Search
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Input
                  label="Name"
                  value={customer.name}
                  onChange={(e) =>
                    setCustomer({ ...customer, name: e.target.value })
                  }
                />
                <Input
                  label="Email"
                  value={customer.email}
                  onChange={(e) =>
                    setCustomer({ ...customer, email: e.target.value })
                  }
                />
                <Input
                  label="Address"
                  value={customer.address}
                  onChange={(e) =>
                    setCustomer({ ...customer, address: e.target.value })
                  }
                />
                <Input
                  label="Pincode"
                  value={customer.pincode}
                  onChange={(e) =>
                    setCustomer({ ...customer, pincode: e.target.value })
                  }
                />
                <Input
                  label="State"
                  placeholder="e.g. Kerala"
                  value={customer.state}
                  onChange={(e) =>
                    setCustomer({ ...customer, state: e.target.value })
                  }
                />
                <Input
                  label="GSTIN (optional)"
                  value={customer.gstNumber}
                  onChange={(e) =>
                    setCustomer({ ...customer, gstNumber: e.target.value })
                  }
                />
              </div>
            </div>

            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Products
              </h3>

              <Dropdown
                label="Add Product"
                options={products.map((p) => ({
                  label: `${p.name} (${p.sku || p.productId})`,
                  value: p._id || "",
                }))}
                onChange={(val) => handleAddProduct(val)}
                value=""
              />

              {cart.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      { key: "name", header: "Product" },
                      {
                        key: "quantity",
                        header: "Qty",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "sellingPrice",
                        header: "Price",
                        align: "right",
                        accessor: ({ row }) =>
                          currencyFormatter(row.sellingPrice),
                      },
                      {
                        key: "discountPercent",
                        header: "Disc (%)",
                        align: "center",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.discountPercent?.toString() || ""}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "discountPercent",
                                parseFloat(e.target.value) || 0
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "taxPercent",
                        header: "GST %",
                        align: "center",
                        accessor: ({ row }) =>
                          `${row.taxPercent ?? 0}%`,
                      },
                      {
                        key: "totalPrice",
                        header: "Total",
                        align: "right",
                        accessor: ({ row }) =>
                          currencyFormatter(row.totalPrice.toFixed(2)),
                      },
                      {
                        key: "action",
                        header: "",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={cart}
                  />
                </div>
              )}
            </div>

            {/* ---------------- PAYMENT SUMMARY ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Payment Summary
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Input
                  label="Tax"
                  value={currencyFormatter(taxValue)}
                  readOnly
                />
                <Input
                  label="Discount (%)"
                  type="number"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(e.target.value)}
                  placeholder="Enter discount %"
                />
                <Dropdown
                  label="Payment Mode"
                  value={paymentMode}
                  options={[
                    { label: "Cash", value: "cash" },
                    { label: "Card", value: "card" },
                    { label: "UPI", value: "upi" },
                    { label: "Credit", value: "credit" },
                  ]}
                  onChange={(val) => setPaymentMode(val)}
                />
                <Dropdown
                  label="Payment Status"
                  value={paymentStatus}
                  options={[{ label: "Paid", value: "paid" }]}
                  onChange={(val) => setPaymentStatus(val)}
                  disabled
                />
              </div>

              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />

              <div className="flex justify-end border-t pt-3 mt-2">
                <table>
                  <tr>
                    <td className="py-0.5 text-sm">Subtotal:</td>
                    <td className="text-sm text-right">
                      {currencyFormatter(subTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-sm">Tax:</td>
                    <td className="text-sm text-right">
                      {currencyFormatter(taxValue)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-sm">
                      Discount ({discountPercentValue}%):
                    </td>
                    <td className="text-sm text-right">
                      {currencyFormatter(discountAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5 font-semibold text-gray-800 text-lg">
                      Grand Total:
                    </td>
                    <td className="text-right font-semibold text-gray-800 text-lg pl-4">
                      {currencyFormatter(grandTotal)}
                    </td>
                  </tr>
                </table>
              </div>

              {/* <div className="flex justify-end border-t pt-3 mt-2">
                <div className="text-right space-y-1">
                  <div className="text-sm text-gray-600">
                    Subtotal: {currencyFormatter(subTotal)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Tax ({TAX_PERCENT}%): {currencyFormatter(taxValue)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Discount ({discountPercentValue}%):{" "}
                    {currencyFormatter(discountAmount)}
                  </div>
                  <div className="font-semibold text-gray-800 text-lg">
                    Grand Total: {currencyFormatter(grandTotal)}
                  </div>
                </div>
              </div> */}
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit("draft")}
                variant="outline"
                isLoading={
                  createSaleOrder.loading ||
                  editSaleOrder.loading ||
                  isLoadingDetails
                }
                disabled={isLoadingDetails}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit("completed")}
                className="bg-indigo-600 text-white"
                isLoading={
                  createSaleOrder.loading ||
                  editSaleOrder.loading ||
                  isLoadingDetails
                }
                disabled={isLoadingDetails}
              >
                Place Order
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SalesForm;
